import { ArtFileType } from "@sticker-giant/sg-frontend-common";
import { IconCloudUpload, IconLoader2 } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { trackArtworkUploaded, trackUploadArtworkClicked } from "@/lib/analytics/segment";
import { ACCEPTED_FILE_TYPES, getUploadError } from "@/lib/product/imageUploadValidation";
import { usePdpStore } from "@/stores/usePdpStore";
import { useServicesStore } from "@/stores/useServicesStore";
interface Props {
  labelId?: string;
}
export default function ImageUploadDropzone({
  labelId
}: Props) {
  const setUploadedFileInfo = usePdpStore(s => s.setUploadedFileInfo);
  const isUploading = usePdpStore(s_0 => s_0.isUploadingArt);
  const setIsUploading = usePdpStore(s_1 => s_1.setIsUploadingArt);
  const setImageWidth = usePdpStore(s_2 => s_2.setImageWidth);
  const setImageHeight = usePdpStore(s_3 => s_3.setImageHeight);
  const artClient = useServicesStore(s_4 => s_4.artClientPublic);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string>();
  function extractImageDimensions(file: File) {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImageWidth(img.naturalWidth);
      setImageHeight(img.naturalHeight);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }
  async function handleFileSelected(file_0: File) {
    const validationError = getUploadError(file_0);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(undefined);
    setIsUploading(true);
    extractImageDimensions(file_0);
    try {
      const result = await artClient?.uploadArt(file_0, ArtFileType.CUSTOMER_ARTWORK);
      setUploadedFileInfo(result);
      if (result) {
        trackArtworkUploaded({
          filename: file_0.name,
          filetype: file_0.type,
          filesize: file_0.size,
          url: result.filepath
        });
      }
    } catch {
      setError("Upload failed. Please try again.");
      setUploadedFileInfo(undefined);
    } finally {
      setIsUploading(false);
    }
  }
  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file_1 = e.dataTransfer.files[0];
    if (file_1) handleFileSelected(file_1);
  }
  function handleChange(e_0: React.ChangeEvent<HTMLInputElement>) {
    const file_2 = e_0.target.files?.[0];
    if (file_2) handleFileSelected(file_2);
  }
  return <div className="flex flex-col gap-2">
      <input ref={inputRef} type="file" accept={ACCEPTED_FILE_TYPES} onChange={handleChange} aria-label="Upload artwork file" className="hidden" />
      <button type="button" aria-labelledby={labelId} disabled={isUploading} onClick={() => {
      trackUploadArtworkClicked();
      inputRef.current?.click();
    }} onDragOver={e_1 => {
      e_1.preventDefault();
      setDragging(true);
    }} onDragLeave={() => setDragging(false)} onDrop={handleDrop} className={`flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed bg-background p-3 py-4 transition-colors ${dragging ? "border-primary bg-primary/5" : "border-input"} ${isUploading ? "cursor-wait opacity-60" : ""}`}>
        {isUploading ? <IconLoader2 size={32} className="animate-spin text-foreground" /> : <IconCloudUpload size={32} className="text-foreground" />}
        <p className="text-xs text-foreground">
          Drag and drop or click to upload your artwork now or submit after you place your order.
        </p>
      </button>
      {error && <p role="alert" className="text-center text-xs text-destructive">
          {error}
        </p>}
      <p className="text-center text-xs leading-4 text-foreground">File Formats Accepted: {ACCEPTED_FILE_TYPES}</p>
    </div>;
}